import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Copy, CheckCircle2, Building2, ArrowLeft, QrCode, Smartphone, MapPin, User, Truck, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const BANK = {
  account_name: "Batra Electronics",
  account_number: "39313150523",
  account_type: "Current A/c",
  bank: "State Bank of India",
  branch: "Main Branch, Dehradun",
  ifsc: "SBIN0000630",
};

const UPI = {
  vpa: "9837095555@okbizaxis",
  name: "Batra Electronics",
  phone: "+91 98370 95555",
};

export default function BankDetails() {
  const { items, subtotal, clear } = useCart();
  const [saving, setSaving] = useState(false);
  const [billing, setBilling] = useState(() => {
    let b = {};
    try { b = JSON.parse(localStorage.getItem("aurea_buyer") || "{}"); } catch { b = {}; }
    return { name: b.name || "", email: b.email || "", phone: b.phone || "", address: "", city: "", state: "", pincode: "", gstin: "", company: "" };
  });
  const [shipSameAsBill, setShipSameAsBill] = useState(true);
  const [shipping, setShipping] = useState({ name: "", phone: "", address: "", city: "", state: "", pincode: "" });
  const [notes, setNotes] = useState("");

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };
  const confirm = async () => {
    // basic guardrails
    if (!billing.name || !billing.email || !billing.phone || !billing.address || !billing.city || !billing.pincode) {
      toast.error("Please fill your billing name, email, phone and full address.");
      const el = document.querySelector('[data-testid="billing-name"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (!shipSameAsBill && (!shipping.name || !shipping.address || !shipping.city || !shipping.pincode)) {
      toast.error("Please fill the shipping address or tick 'Same as billing'.");
      return;
    }
    setSaving(true);
    try {
      const shipFinal = shipSameAsBill ? { ...billing } : shipping;
      const payload = {
        name: billing.name,
        email: billing.email,
        phone: billing.phone,
        subject: `Bank transfer for ₹${subtotal.toLocaleString('en-IN')}`,
        message: `Order details:\n${JSON.stringify({ items, subtotal_incl_gst: subtotal, gst_amount: Math.round(subtotal - subtotal / 1.18), base_price: Math.round(subtotal / 1.18) }, null, 2)}\n\nBilling:\n${JSON.stringify(billing, null, 2)}\n\nShipping:\n${JSON.stringify(shipFinal, null, 2)}\n\nNotes: ${notes || "—"}`,
        kind: "booking",
        details: { items, subtotal, billing, shipping: shipFinal, notes, gstin: billing.gstin || null },
      };
      await api.post("/inquiries", payload);
      toast.success("Payment initiated — we'll confirm within 24h");
      clear();
      // brief pause so toast is seen
      setTimeout(() => { window.location.href = "/payment/success"; }, 1400);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setSaving(false); }
  };

  // UPI deeplink — opens installed UPI app (GPay/PhonePe/Paytm/BHIM) with pre-filled amount + note
  const upiLink = `upi://pay?pa=${encodeURIComponent(UPI.vpa)}&pn=${encodeURIComponent(UPI.name)}&am=${subtotal}&cu=INR&tn=${encodeURIComponent("HomeTheatres India Order")}`;

  const Row = ({ label, value, testid }) => (
    <div className="flex items-center justify-between border-b border-white/5 py-3">
      <div className="min-w-0">
        <div className="text-xs text-white/40 uppercase tracking-wider">{label}</div>
        <div className="text-white font-medium mt-0.5 break-all">{value}</div>
      </div>
      <button onClick={() => copy(value)} className="p-2 text-white/50 hover:text-gold shrink-0" data-testid={testid}><Copy className="w-4 h-4" /></button>
    </div>
  );

  const Field = ({ label, required, value, onChange, placeholder, type = "text", testid, colSpan = 1 }) => (
    <div className={colSpan === 2 ? "sm:col-span-2" : ""}>
      <label className="text-xs text-white/50 block mb-1.5">{label}{required && " *"}</label>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} required={required} data-testid={testid} />
    </div>
  );

  return (
    <div className="pt-32 pb-24 container-x max-w-4xl mx-auto" data-testid="bank-details-page">
      <Link to="/cart" className="text-sm text-white/50 hover:text-gold inline-flex items-center gap-2 mb-6"><ArrowLeft className="w-4 h-4" /> Back to cart</Link>
      <div className="kicker mb-3">Payment</div>
      <h1 className="font-display text-4xl md:text-5xl">Scan · Pay · <em className="not-italic gold-text">Done.</em></h1>
      <p className="mt-4 text-white/60 max-w-xl">Pay HomeTheatres India instantly via UPI (Google Pay · PhonePe · Paytm · BHIM) or via direct bank transfer. Once paid, tap "I've Paid" and our team confirms within 24 hours.</p>

      {/* BILLING + SHIPPING form */}
      <div className="glass rounded-3xl p-8 mt-8" data-testid="billing-section">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl bg-gold/10 flex items-center justify-center text-gold"><User className="w-5 h-5" /></div>
          <div>
            <div className="kicker mb-1">Billing Details</div>
            <div className="text-xs text-white/50">Where should we invoice this order?</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Full name" required value={billing.name} onChange={(e) => setBilling({ ...billing, name: e.target.value })} placeholder="Your name" testid="billing-name" />
          <Field label="Email" required type="email" value={billing.email} onChange={(e) => setBilling({ ...billing, email: e.target.value })} placeholder="you@example.com" testid="billing-email" />
          <Field label="Phone" required type="tel" value={billing.phone} onChange={(e) => setBilling({ ...billing, phone: e.target.value })} placeholder="+91 —" testid="billing-phone" />
          <Field label="Company (if business)" value={billing.company} onChange={(e) => setBilling({ ...billing, company: e.target.value })} placeholder="Company name — optional" testid="billing-company" />
          <Field label="Billing address" required value={billing.address} onChange={(e) => setBilling({ ...billing, address: e.target.value })} placeholder="Flat/Building, Street, Landmark" testid="billing-address" colSpan={2} />
          <Field label="City" required value={billing.city} onChange={(e) => setBilling({ ...billing, city: e.target.value })} placeholder="e.g. Dehradun" testid="billing-city" />
          <Field label="State" required value={billing.state} onChange={(e) => setBilling({ ...billing, state: e.target.value })} placeholder="e.g. Uttarakhand" testid="billing-state" />
          <Field label="PIN code" required value={billing.pincode} onChange={(e) => setBilling({ ...billing, pincode: e.target.value })} placeholder="6-digit" testid="billing-pincode" />
          <Field label="GSTIN (for business billing — optional)" value={billing.gstin} onChange={(e) => setBilling({ ...billing, gstin: e.target.value.toUpperCase() })} placeholder="15-digit GST number" testid="billing-gstin" />
        </div>
      </div>

      <div className="glass rounded-3xl p-8 mt-6" data-testid="shipping-section">
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gold/10 flex items-center justify-center text-gold"><Truck className="w-5 h-5" /></div>
            <div>
              <div className="kicker mb-1">Shipping / Delivery Address</div>
              <div className="text-xs text-white/50">Where should we deliver &amp; install?</div>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
            <input type="checkbox" checked={shipSameAsBill} onChange={(e) => setShipSameAsBill(e.target.checked)} data-testid="ship-same-as-bill" />
            Same as billing
          </label>
        </div>
        {!shipSameAsBill && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="shipping-fields">
            <Field label="Recipient name" required value={shipping.name} onChange={(e) => setShipping({ ...shipping, name: e.target.value })} placeholder="Name at delivery" testid="shipping-name" />
            <Field label="Recipient phone" type="tel" value={shipping.phone} onChange={(e) => setShipping({ ...shipping, phone: e.target.value })} placeholder="+91 —" testid="shipping-phone" />
            <Field label="Shipping address" required value={shipping.address} onChange={(e) => setShipping({ ...shipping, address: e.target.value })} placeholder="Flat/Building, Street, Landmark" testid="shipping-address" colSpan={2} />
            <Field label="City" required value={shipping.city} onChange={(e) => setShipping({ ...shipping, city: e.target.value })} placeholder="City" testid="shipping-city" />
            <Field label="State" value={shipping.state} onChange={(e) => setShipping({ ...shipping, state: e.target.value })} placeholder="State" testid="shipping-state" />
            <Field label="PIN code" required value={shipping.pincode} onChange={(e) => setShipping({ ...shipping, pincode: e.target.value })} placeholder="6-digit" testid="shipping-pincode" />
          </div>
        )}
        {shipSameAsBill && billing.address && (
          <div className="text-sm text-white/60 mt-3" data-testid="shipping-preview">
            Delivering to: <span className="text-white">{[billing.address, billing.city, billing.state, billing.pincode].filter(Boolean).join(", ")}</span>
          </div>
        )}
      </div>

      <div className="glass rounded-3xl p-8 mt-6" data-testid="notes-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-gold/10 flex items-center justify-center text-gold"><FileText className="w-5 h-5" /></div>
          <div>
            <div className="kicker mb-1">Notes for our team</div>
            <div className="text-xs text-white/50">Preferred delivery time, gate/parking instructions, install prefs — anything we should know.</div>
          </div>
        </div>
        <textarea rows="4" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional — e.g. 'Deliver after 6 pm, second-floor apartment, please call before arrival.'" data-testid="order-notes"></textarea>
      </div>

      {/* AMOUNT — always visible at top */}
      <div className="glass rounded-3xl p-6 mt-8 flex items-center justify-between flex-wrap gap-4" data-testid="amount-banner">
        <div>
          <div className="kicker mb-1">Amount to Pay</div>
          <div className="font-display text-5xl gold-text">₹{subtotal.toLocaleString('en-IN')}</div>
        </div>
        <div className="text-xs text-white/50 max-w-xs">Include your name and phone number as the payment reference so we can match it to your order.</div>
      </div>

      {/* UPI QR — Primary payment method */}
      <div className="grid md:grid-cols-[auto,1fr] gap-6 mt-6" data-testid="upi-section">
        <div className="glass rounded-3xl p-6 flex flex-col items-center">
          <div className="kicker mb-4 flex items-center gap-2"><QrCode className="w-3.5 h-3.5" /> Scan &amp; Pay</div>
          <div className="rounded-2xl overflow-hidden bg-white p-2 shadow-lg" style={{ maxWidth: 260 }}>
            <img src="/assets/upi-qr.jpg" alt="UPI QR code" className="w-full h-auto" data-testid="upi-qr-image" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mt-3" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Works with any UPI app</div>
        </div>

        <div className="glass rounded-3xl p-6 flex flex-col">
          <div className="kicker mb-4 flex items-center gap-2"><Smartphone className="w-3.5 h-3.5" /> On Mobile?</div>
          <p className="text-sm text-white/70 leading-relaxed mb-5">Tap the button below and your default UPI app (GPay, PhonePe, Paytm, BHIM) will open with the amount pre-filled.</p>
          <a href={upiLink} className="btn-gold justify-center text-center" data-testid="pay-via-upi-btn">Open UPI App to Pay ₹{subtotal.toLocaleString('en-IN')}</a>

          <div className="hairline my-6"></div>

          <Row label="UPI ID / VPA" value={UPI.vpa} testid="copy-upi-vpa" />
          <Row label="Name" value={UPI.name} testid="copy-upi-name" />
          <Row label="Phone" value={UPI.phone} testid="copy-upi-phone" />

          <div className="mt-5 flex items-center gap-3 flex-wrap text-[10px] uppercase tracking-[0.22em] text-white/40" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            <span>Accepts</span>
            <span>· G Pay</span>
            <span>· PhonePe</span>
            <span>· Paytm</span>
            <span>· BHIM</span>
            <span>· Amazon Pay</span>
          </div>
        </div>
      </div>

      {/* Bank transfer — Alternate method */}
      <div className="glass rounded-3xl p-8 mt-6" data-testid="bank-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-gold/10 flex items-center justify-center text-gold"><Building2 className="w-5 h-5" /></div>
          <div>
            <div className="kicker mb-1">Or — Direct Bank Transfer (NEFT · IMPS · RTGS)</div>
            <div className="font-display text-xl">{BANK.bank}</div>
            <div className="text-xs text-white/50">{BANK.branch}</div>
          </div>
        </div>
        <Row label="Account Name" value={BANK.account_name} testid="copy-bank-name" />
        <Row label="Account Type" value={BANK.account_type} />
        <Row label="Account Number" value={BANK.account_number} testid="copy-bank-account" />
        <Row label="IFSC Code" value={BANK.ifsc} testid="copy-bank-ifsc" />
      </div>

      {/* Order Summary */}
      <div className="glass rounded-3xl p-8 mt-6" data-testid="order-summary">
        <div className="kicker mb-3">Order Summary</div>
        {items.map((i) => (
          <div key={i.key} className="flex justify-between py-2 text-sm">
            <span className="text-white/70">{i.name}{i.color && <span className="text-white/50" data-testid={`summary-color-${i.key}`}> · {i.color}</span>} × {i.quantity}</span>
            <span>₹{(i.price * i.quantity).toLocaleString('en-IN')}</span>
          </div>
        ))}
        <div className="hairline my-4"></div>
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between text-white/60"><span>Base price</span><span>₹{Math.round(subtotal / 1.18).toLocaleString('en-IN')}</span></div>
          <div className="flex justify-between text-white/60"><span>GST @ 18% (included)</span><span>₹{Math.round(subtotal - subtotal / 1.18).toLocaleString('en-IN')}</span></div>
          <div className="flex justify-between text-white/60"><span>Shipping</span><span className="text-gold">Free · India-wide</span></div>
        </div>
        <div className="hairline my-4"></div>
        <div className="flex justify-between text-xl font-display">
          <span>Total (incl. GST)</span>
          <span className="text-gold">₹{subtotal.toLocaleString('en-IN')}</span>
        </div>
        <div className="text-[10px] text-white/40 mt-3 leading-relaxed">All prices are inclusive of 18% GST. A GST invoice{billing.gstin ? ` (GSTIN: ${billing.gstin})` : ""} will be issued at delivery.</div>
      </div>

      <p className="text-xs text-white/40 mt-6 text-center">Email the payment screenshot / UTR to <a href="mailto:batraelectronics55@gmail.com" className="text-gold hover:underline">batraelectronics55@gmail.com</a> along with your name &amp; phone so we can match your order faster.</p>

      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        <button onClick={confirm} disabled={saving} className="btn-gold disabled:opacity-60" data-testid="confirm-paid-btn">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving order…</> : <><CheckCircle2 className="w-4 h-4" /> I&apos;ve Made the Payment</>}
        </button>
        <Link to="/cart" className="btn-ghost-outline">Back to Cart</Link>
      </div>
    </div>
  );
}
